import { CellContext } from '@tanstack/react-table';
import classNames from 'classnames';
import { InterestSourcePill } from 'components/interest-source-pill';
import { Date } from 'components/date';
import { format0, formatNumber, formatWholeDollarCurrency } from 'utils/number';
import { Calculation, Checkmark, Edit } from '@carbon/react/icons';
import { TypeCurveVerificationStatusIndicator } from 'components/type-curve-verification-status/type-curve-verification-status';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { Link } from 'components/link';
import { UnvaluedReasonCell } from 'components/unvalued-reason';
import {
  AfeSourceConfig,
  CostDateField,
  SlotDateField,
  SubtractNRI,
  TractNRI,
  VolumeSourceConfig,
} from './types';
import {
  formatTitleRiskValue,
  isSameDate,
} from '../../pages/crm-tracts-subtracts/utils';
import styles from './nri-table.module.scss';
import { CellRenderer } from './nri-table';
import { isNil } from 'lodash-es';
import { NRIVerification } from './nri-verification';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { TotalUnitInterestCard } from 'components/total-unit-interest-card';
import { TractNriMatrixCard } from 'components/tract-nri-matrix-card';
import { UatNriMatrixCard } from 'components/uat-nri-matrix-card';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card';
import { OverAllocationWarning } from './over-allocation-warning';

const formatPercentage = (value: number) => {
  return formatNumber(value * 100, 2, 2, 'decimal', false, '--', '%');
};

// Variable precision (0-8 decimals) - strips trailing zeros
const formatVariable = (value: number | null | undefined) =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 8,
    includeCommas: true,
  });

// Currency formatting - $1,234,567.89
const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '--';
  return `$${formatNumber({
    value,
    minPrecision: 2,
    maxPrecision: 2,
    includeCommas: true,
  })}`;
};

// Render trigger for calculation icon
const renderCalculationTrigger = (active: boolean, toggle: () => void) => (
  <CalculationIconTrigger
    definition="Show Calculation"
    active={active}
    onClick={toggle}
  >
    <div className={styles.iconContainer}>
      <Calculation aria-hidden="true" role="img" />
    </div>
  </CalculationIconTrigger>
);

// Helper - converts atom values to verification status text (exported for nri-table.tsx)
export const getVerificationStatusText = (
  totalSteps: number,
  markedCorrectTotal: number,
  markedIncorrectTotal: number
): string => {
  if (totalSteps === 0) return 'Not Started';
  if (markedIncorrectTotal > 0) return 'Incorrect';
  if (markedCorrectTotal === totalSteps) return 'Correct';
  if (markedCorrectTotal > 0) return 'In Progress';
  return 'Not Started';
};

/**
 * Shared cell renderer for cost date columns.
 * Displays overridden date, checkmark icon, and edit button.
 * @param {CellContext<TractNRI, string>} info - The cell context from TanStack Table.
 * @param {string} dateField - The cost date field key.
 * @param {string} label - The display label for the cost date.
 * @return {JSX.Element} The rendered cost date cell.
 */
const costDateCell = (
  info: CellContext<TractNRI, string>,
  dateField: CostDateField,
  label: string
) => {
  const value = info.getValue();
  const row = info.row.original;
  const afeSourceConfig = info.table.options.meta?.afeSourceConfig as
    AfeSourceConfig | undefined;
  const onEditCostDate = afeSourceConfig?.onEditCostDate;
  const isEditDisabled = afeSourceConfig?.isEditDisabled;
  const overriddenCostDates = afeSourceConfig?.overriddenCostDates ?? {};

  const overriddenDate =
    row.slotId && overriddenCostDates[row.slotId]?.[dateField];
  const displayValue = overriddenDate || value;

  return (
    <div
      className={classNames(styles.volumeSourceCell, {
        'nri-configured-cell': Boolean(overriddenDate),
      })}
    >
      {overriddenDate && (
        <Checkmark
          className={styles.overrideIcon}
          aria-label={`${label} overridden`}
        />
      )}
      <span className={styles.volumeSourceText}>
        {displayValue ? <Date date={displayValue} format="MM/DD/YYYY" /> : '--'}
      </span>
      {onEditCostDate && row.slotId && (
        <FloatingTooltip content={`Edit ${label}`} placement="top">
          <button
            type="button"
            className={styles.editButton}
            onClick={(e) => {
              e.stopPropagation();
              if (row.slotId) onEditCostDate(row.slotId, dateField);
            }}
            disabled={isEditDisabled}
            aria-label={`Edit ${label.toLowerCase()}`}
          >
            <Edit />
          </button>
        </FloatingTooltip>
      )}
    </div>
  );
};

/**
 * Renders a slot date cell (permit/spud/first production) with optional edit pencil icon.
 * @param {CellContext<TractNRI, string>} info - The cell context from TanStack Table.
 * @param {SlotDateField} dateField - The slot date field key.
 * @param {string} label - The display label for the date.
 * @return {JSX.Element} The rendered slot date cell.
 */
const slotDateCell = (
  info: CellContext<TractNRI, string>,
  dateField: SlotDateField,
  label: string
) => {
  const value = info.getValue();
  const row = info.row.original;
  const afeSourceConfig = info.table.options.meta?.afeSourceConfig as
    AfeSourceConfig | undefined;
  const onEditSlotDate = afeSourceConfig?.onEditSlotDate;
  const overriddenSlotDates = afeSourceConfig?.overriddenSlotDates ?? {};

  const overriddenDate =
    row.slotId && overriddenSlotDates[row.slotId]?.[dateField];
  const displayValue = overriddenDate || value;

  return (
    <div
      className={classNames(styles.volumeSourceCell, {
        'nri-configured-cell': Boolean(overriddenDate),
      })}
    >
      {overriddenDate && (
        <Checkmark
          size={16}
          className={styles.overrideIcon}
          aria-label={`${label} overridden`}
        />
      )}
      <span className={styles.volumeSourceText}>
        {displayValue ? <Date date={displayValue} format="MM/DD/YYYY" /> : '--'}
      </span>
      {onEditSlotDate && row.slotId && (
        <FloatingTooltip content={`Edit ${label}`} placement="top">
          <button
            type="button"
            className={styles.editButton}
            onClick={(e) => {
              e.stopPropagation();
              if (row.slotId) onEditSlotDate(row.slotId, dateField);
            }}
            aria-label={`Edit ${label.toLowerCase()}`}
          >
            <Edit size={16} />
          </button>
        </FloatingTooltip>
      )}
    </div>
  );
};

export const tractNRIColumnConfig = {
  crmTract: {
    header: 'CRM Tract',
    cell: (info: CellContext<TractNRI, string>) => {
      const row = info.row.original;
      const value = info.getValue();
      // Link to the full-page CRM Tract Wizard edit route in a new tab.
      // Replaces the prior `<a href="#" onClick={onTractClick}>` pattern
      // which opened the legacy in-page modal and rendered as a broken
      // link (`.../eval3/N#`) when `onTractClick` wasn't wired by the
      // parent. `crmTractSalesforceId` is the tract's Salesforce id.
      if (row.crmTractSalesforceId && value) {
        return (
          <Link
            to={`/crm/tracts_and_subtracts/${encodeURIComponent(
              row.crmTractSalesforceId
            )}/edit`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.cellLabel}
          >
            {value}
          </Link>
        );
      }

      return <CellRenderer>{value}</CellRenderer>;
    },
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  // The tract's aliases (tags) as comma text, read when the page is read; blank with none (INS-13689).
  // A text column so the table's search finds a row by a tag.
  crmTractTags: {
    header: 'Tract Tags',
    cell: (info: CellContext<TractNRI, string | undefined>) => (
      <CellRenderer>{info.getValue() ?? ''}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  crmAccount: {
    header: 'CRM Account',
    cell: (info: CellContext<TractNRI, string>) => {
      const row = info.row.original;
      return (
        <CellRenderer href={row.crmAccountHref}>{info.getValue()}</CellRenderer>
      );
    },
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  slotId: {
    header: 'Slot ID',
    cell: (info: CellContext<TractNRI, number>) => {
      const row = info.row.original;
      return (
        <CellRenderer href={row.slotIdHref}>{info.getValue()}</CellRenderer>
      );
    },
    size: 250,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    filterFn: 'includesString',
    meta: {
      filterVariant: 'text',
    },
  },
  assetType: {
    header: 'Asset Type',
    cell: (info: CellContext<TractNRI, 'wellbore' | 'ltd_slot' | null>) => {
      const value = info.getValue();
      let label: string;
      if (value === 'wellbore') {
        label = 'Wellbore';
      } else if (value === 'ltd_slot') {
        label = 'LTD Slot';
      } else {
        label = 'Tract';
      }
      return <CellRenderer>{label}</CellRenderer>;
    },
    size: 120,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  unit: {
    header: 'Unit',
    cell: (info: CellContext<TractNRI, string>) => {
      const row = info.row.original;
      return <CellRenderer href={row.unitHref}>{info.getValue()}</CellRenderer>;
    },
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  // The unit's aliases (tags) as comma text (INS-13689); see crmTractTags.
  unitTags: {
    header: 'Unit Tags',
    cell: (info: CellContext<TractNRI, string | undefined>) => (
      <CellRenderer>{info.getValue() ?? ''}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  // Per-row pricing columns (Eval 3.0). Display-only — sorting/filtering
  // are intentionally disabled because the BE allowlist for sort/filter
  // does not include these computed fields.
  targetNpvExcluded: {
    header: 'Target - Broker',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatWholeDollarCurrency(info.getValue())}</CellRenderer>
    ),
    size: 160,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    enableSorting: false,
  },
  maxNpvExcluded: {
    header: 'Max - Broker',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatWholeDollarCurrency(info.getValue())}</CellRenderer>
    ),
    size: 160,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    enableSorting: false,
  },
  targetNpvIncluded: {
    header: 'Target - SIM',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatWholeDollarCurrency(info.getValue())}</CellRenderer>
    ),
    size: 160,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    enableSorting: false,
  },
  maxNpvIncluded: {
    header: 'Max - SIM',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatWholeDollarCurrency(info.getValue())}</CellRenderer>
    ),
    size: 160,
    enableColumnFilter: false,
    enableGlobalFilter: false,
    enableSorting: false,
  },
  totalSlotNri: {
    header: 'Total Slot NRI',
    cell: (info: CellContext<TractNRI, number>) => {
      const value = info.getValue();
      const row = info.row.original;
      const subtracts = row.subtracts ?? [];
      const tractId = row.crmTractSalesforceId;
      const slotId = row.slotId;
      const isCertified = row.wiSource === 'certified';

      const onTractClick = info.table.options.meta?.onTractClick as
        ((tractId: string, subtractId?: string) => void) | undefined;

      const nriItems = subtracts.map((s) => ({
        subtract: s.crmSubtractId ?? '',
        subtractSalesforceId: s.crmSubtractSalesforceId,
        tractSalesforceId: s.crmTractSalesforceId,
        uat: s.uat ?? '',
        value: s.allocationValue,
        valueUnit: s.allocationValueUnit,
        factor: s.allocationFactor,
        uatInterest: s.uatNri ?? 0,
        nriCalculationId: s.nriCalculationId,
      }));

      return (
        <div className={styles.columnActionContainer}>
          {formatNumberWithTooltip(value, 8)}
          <OverAllocationWarning value={value} measure="NRI" />
          <InterestSourcePill
            wiSource={row.wiSource}
            nriCertifiedWorkspaceId={row.nriCertifiedWorkspaceId}
            interestEntityId={row.interestEntityId}
          />
          {subtracts.length > 0 && !isCertified && (
            <TotalUnitInterestCard
              slotName={row.wellName ?? ''}
              items={nriItems}
              calculatedValue={value ?? 0}
              calculationType="NRI"
              onTractClick={onTractClick}
            />
          )}
          {tractId && slotId && !isCertified && (
            <TractNriMatrixCard
              tractId={tractId}
              slotId={row.wellStatus === 'LTD' ? slotId : undefined}
              wellUwi={row.wellStatus !== 'LTD' ? slotId : undefined}
              calculationType="NRI"
            />
          )}
        </div>
      );
    },
    size: 400,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  totalSlotWi: {
    header: 'Total Slot WI',
    cell: (info: CellContext<TractNRI, number>) => {
      const value = info.getValue();
      const row = info.row.original;
      const subtracts = row.subtracts ?? [];
      const tractId = row.crmTractSalesforceId;
      const slotId = row.slotId;
      const isCertified = row.wiSource === 'certified';
      const onTractClick = info.table.options.meta?.onTractClick as
        ((tractId: string, subtractId?: string) => void) | undefined;

      const wiItems = subtracts.map((s) => ({
        subtract: s.crmSubtractId ?? '',
        subtractSalesforceId: s.crmSubtractSalesforceId,
        tractSalesforceId: s.crmTractSalesforceId,
        uat: s.uat ?? '',
        value: s.allocationValue,
        valueUnit: s.allocationValueUnit,
        factor: s.allocationFactor,
        uatInterest: s.uatWi ?? 0,
        nriCalculationId: s.nriCalculationId,
      }));

      return (
        <div className={styles.columnActionContainer}>
          {formatNumberWithTooltip(value, 8)}
          <OverAllocationWarning value={value} measure="WI" />
          <InterestSourcePill
            wiSource={row.wiSource}
            nriCertifiedWorkspaceId={row.nriCertifiedWorkspaceId}
            interestEntityId={row.interestEntityId}
          />
          {subtracts.length > 0 && !isCertified && (
            <TotalUnitInterestCard
              slotName={row.wellName ?? ''}
              items={wiItems}
              calculatedValue={value ?? 0}
              calculationType="WI"
              onTractClick={onTractClick}
            />
          )}
          {tractId && slotId && !isCertified && (
            <TractNriMatrixCard
              tractId={tractId}
              slotId={row.wellStatus === 'LTD' ? slotId : undefined}
              wellUwi={row.wellStatus !== 'LTD' ? slotId : undefined}
              calculationType="WI"
            />
          )}
        </div>
      );
    },
    size: 400,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  allocationMethod: {
    header: 'Allocation Method',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  tractNetAcres: {
    header: 'Tract Net Acres',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  netAcreageUnits: {
    header: 'Net Acreage Units',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  tractGrossAcres: {
    header: 'Tract Gross Acres',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  surveyArea: {
    header: 'Survey Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 350,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  stateAbbreviation: {
    header: 'State',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 100,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  quarterCall: {
    header: 'Quarter Call',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  instrumentType: {
    header: 'Instrument Type',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  interestType: {
    header: 'Interest Type',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  depthRange: {
    header: 'Depth Range',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  unitSource: {
    header: 'Unit Source',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  unitDevelopmentType: {
    header: 'Unit Development Type',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 220,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  dpsArea: {
    header: 'DPS Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  taxArea: {
    header: 'Tax Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  differentialArea: {
    header: 'Differential Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },

  discountRateArea: {
    header: 'Discount Rate Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  expenseArea: {
    header: 'Expense Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  wellName: {
    header: 'Well Name',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer href={info.row.original.slotIdHref}>
        {info.getValue()}
      </CellRenderer>
    ),
    size: 350,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  wellStatus: {
    header: 'Well Status',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  typeCurveStatus: {
    header: 'Type Curve Status',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  operator: {
    header: 'Operator',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 350,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  holeDirection: {
    header: 'Hole Direction',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  formation: {
    header: 'Formation',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  slotNumber: {
    header: 'Slot #',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 120,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  permitDate: {
    header: 'Permit Date',
    cell: (info: CellContext<TractNRI, string>) =>
      slotDateCell(info, 'permitDate', 'Permit Date'),
    filterFn: isSameDate,
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  spudDate: {
    header: 'Spud Date',
    cell: (info: CellContext<TractNRI, string>) =>
      slotDateCell(info, 'spudDate', 'Spud Date'),
    filterFn: isSameDate,
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  rigReleaseDate: {
    header: 'Rig Release Date',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      return value ? (
        <CellRenderer>
          <Date date={value} format="MM/DD/YYYY" />
        </CellRenderer>
      ) : (
        <CellRenderer>--</CellRenderer>
      );
    },
    filterFn: isSameDate,
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  completionDate: {
    header: 'Completion Date',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      return value ? (
        <CellRenderer>
          <Date date={value} format="MM/DD/YYYY" />
        </CellRenderer>
      ) : (
        <CellRenderer>--</CellRenderer>
      );
    },
    filterFn: isSameDate,
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  firstProductionDate: {
    header: 'First Production Date',
    cell: (info: CellContext<TractNRI, string>) =>
      slotDateCell(info, 'firstProductionDate', 'First Production Date'),
    filterFn: isSameDate,
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  shutInDate: {
    header: 'Shut In Date',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      return value ? (
        <CellRenderer>
          <Date date={value} format="MM/DD/YYYY" />
        </CellRenderer>
      ) : (
        <CellRenderer>--</CellRenderer>
      );
    },
    filterFn: isSameDate,
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  valued: {
    header: 'Valued',
    cell: (info: CellContext<TractNRI, boolean>) => (
      <CellRenderer>
        {info.getValue() === true
          ? 'Yes'
          : info.getValue() === false
            ? 'No'
            : '--'}
      </CellRenderer>
    ),
    size: 100,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'text',
    },
  },
  unvaluedReason: {
    header: 'Unvalued Reason',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>
        {/* Each fixable reason links (INS-12687); an exclusion only on a package page, which
            passes its package. */}
        <UnvaluedReasonCell
          reason={info.getValue()}
          row={info.row.original}
          packageId={info.table.options.meta?.packageId ?? null}
        />
      </CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  titleQuality: {
    header: 'Title Quality',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{formatTitleRiskValue(info.getValue())}</CellRenderer>
    ),
    size: 130,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  titleRiskFactor: {
    header: 'Title Risk Factor',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{formatTitleRiskValue(info.getValue())}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  basin: {
    header: 'Basin',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  tier: {
    header: 'Tier',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 120,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  typeCurveAreaName: {
    header: 'Type Curve Area',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  trueVerticalDepthFeet: {
    header: 'TVD (ft)',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{format0(info.getValue())}</CellRenderer>
    ),
    size: 130,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  // Volume source columns (Eval 3.0)
  volumeSourceId: {
    header: 'Volume Source',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      const row = info.row.original;
      const volumeSourceConfig = info.table.options.meta?.volumeSourceConfig as
        VolumeSourceConfig | undefined;
      const onEdit = volumeSourceConfig?.onEdit;
      const isEditDisabled = volumeSourceConfig?.isEditDisabled;
      const overriddenSlotIds = volumeSourceConfig?.overriddenSlotIds ?? [];
      const overriddenVolumeSourceIds =
        volumeSourceConfig?.overriddenVolumeSourceIds ?? {};
      const overriddenVolumeSourceHrefs =
        volumeSourceConfig?.overriddenVolumeSourceHrefs ?? {};
      const isOverridden = row.slotId
        ? overriddenSlotIds.includes(row.slotId)
        : false;
      const displayValue =
        row.slotId && overriddenVolumeSourceIds[row.slotId]
          ? overriddenVolumeSourceIds[row.slotId]
          : value;

      // Use overridden href if available, otherwise use the original href
      const volumeSourceHref =
        row.slotId && overriddenVolumeSourceHrefs[row.slotId]
          ? overriddenVolumeSourceHrefs[row.slotId]
          : row.volumeSourceHref;

      // Render link for type_curve sources with valid href
      // If overridden with href, it's a type_curve; otherwise check original type
      const hasOverriddenHref = Boolean(
        row.slotId && overriddenVolumeSourceHrefs[row.slotId]
      );
      const isTypeCurveLink = Boolean(
        volumeSourceHref &&
        (hasOverriddenHref || row.volumeSourceType === 'type_curve')
      );

      const showVerificationIcon =
        isTypeCurveLink || row.volumeSourceType === 'type_curve';
      const verificationStatusIcon = showVerificationIcon ? (
        <TypeCurveVerificationStatusIndicator
          status={row.typeCurveVerificationStatus}
          showLabel={false}
          hoverVerifiedBy
          lastVerifiedBy={row.typeCurveLastVerifiedBy}
          className={styles.volumeSourceVerificationIcon}
          iconClasses={{
            correct: styles.verificationStatusCorrect,
            incorrect: styles.verificationStatusIncorrect,
          }}
        />
      ) : null;

      const displayContent = isTypeCurveLink ? (
        <span className={styles.volumeSourceText}>
          {verificationStatusIcon}
          <Link
            className={styles.volumeSourceValue}
            to={volumeSourceHref!}
            target="_blank"
          >
            {displayValue ?? '--'}
          </Link>
        </span>
      ) : (
        <span className={styles.volumeSourceText}>
          {verificationStatusIcon}
          <span className={styles.volumeSourceValue}>
            {displayValue ?? '--'}
          </span>
        </span>
      );

      return (
        <div className={styles.volumeSourceCell}>
          {isOverridden && (
            <Checkmark
              className={styles.overrideIcon}
              aria-label="Volume source overridden"
            />
          )}
          {displayContent}
          {onEdit && row.slotId && (
            <FloatingTooltip content="Edit Volume Source" placement="top">
              <button
                type="button"
                className={styles.editButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.slotId) onEdit(row.slotId);
                }}
                disabled={isEditDisabled}
                aria-label="Edit volume source"
              >
                <Edit />
              </button>
            </FloatingTooltip>
          )}
        </div>
      );
    },
    size: 280,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  forecastStatus: {
    header: 'Forecast Status',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue() ?? '--'}</CellRenderer>
    ),
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  forecastDate: {
    header: 'Forecast Date',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      return value ? (
        <CellRenderer>
          <Date date={value} format="MM/DD/YYYY" />
        </CellRenderer>
      ) : (
        <CellRenderer>--</CellRenderer>
      );
    },
    filterFn: isSameDate,
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  dcaScenario: {
    header: 'DCA Scenario',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      const row = info.row.original;
      const volumeSourceConfig = info.table.options.meta?.volumeSourceConfig as
        VolumeSourceConfig | undefined;
      const overriddenDcaScenarios =
        volumeSourceConfig?.overriddenDcaScenarios ?? {};
      const displayValue =
        row.slotId && overriddenDcaScenarios[row.slotId]
          ? overriddenDcaScenarios[row.slotId]
          : value;
      return <CellRenderer>{displayValue ?? '--'}</CellRenderer>;
    },
    size: 150,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  slotLength: {
    header: 'Slot Length',
    cell: (info: CellContext<TractNRI, number>) => {
      const value = info.getValue();
      return (
        <CellRenderer>
          {value !== null && value !== undefined
            ? formatNumber({
                value,
                minPrecision: 0,
                maxPrecision: 0,
                includeCommas: true,
              })
            : '--'}
        </CellRenderer>
      );
    },
    size: 120,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  typeCurveLateralLength: {
    header: 'TC Lateral Length',
    cell: (info: CellContext<TractNRI, number>) => {
      const value = info.getValue();
      return (
        <CellRenderer>
          {value !== null && value !== undefined
            ? formatNumber({
                value,
                minPrecision: 0,
                maxPrecision: 0,
                includeCommas: true,
              })
            : '--'}
        </CellRenderer>
      );
    },
    size: 160,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  // AFE source columns (Eval 3.0)
  afeSourceId: {
    header: 'AFE Source',
    cell: (info: CellContext<TractNRI, string>) => {
      const value = info.getValue();
      const row = info.row.original;
      const afeSourceConfig = info.table.options.meta?.afeSourceConfig as
        AfeSourceConfig | undefined;
      const onEdit = afeSourceConfig?.onEditAfeSource;
      const isEditDisabled = afeSourceConfig?.isEditDisabled;
      const overriddenSlotIds = afeSourceConfig?.overriddenAfeSlotIds ?? [];
      const changedSinceRunSlotIds =
        afeSourceConfig?.changedSinceRunAfeSlotIds ?? [];
      const overriddenAfeSourceIds =
        afeSourceConfig?.overriddenAfeSourceIds ?? {};
      const overriddenAfeSourceHrefs =
        afeSourceConfig?.overriddenAfeSourceHrefs ?? {};

      // Checkmark = this AFE is overridden (persistent). Blue cell = the override
      // hasn't been re-run in an eval yet (clears once the eval is re-run).
      const isOverridden = row.slotId
        ? overriddenSlotIds.includes(row.slotId)
        : false;
      const isChangedSinceRun = row.slotId
        ? changedSinceRunSlotIds.includes(row.slotId)
        : false;

      const displayValue =
        row.slotId && overriddenAfeSourceIds[row.slotId]
          ? overriddenAfeSourceIds[row.slotId]
          : value;

      const afeHref =
        row.slotId && overriddenAfeSourceHrefs[row.slotId]
          ? overriddenAfeSourceHrefs[row.slotId]
          : row.afeSourceHref;

      const displayContent = afeHref ? (
        <span className={styles.volumeSourceText}>
          <Link
            className={styles.volumeSourceValue}
            to={afeHref}
            target="_blank"
          >
            {displayValue ?? '--'}
          </Link>
        </span>
      ) : (
        <span className={styles.volumeSourceText}>
          <span className={styles.volumeSourceValue}>
            {displayValue ?? '--'}
          </span>
        </span>
      );

      return (
        <div
          className={classNames(styles.volumeSourceCell, {
            'nri-configured-cell': isChangedSinceRun,
          })}
        >
          {isOverridden && (
            <Checkmark
              className={styles.overrideIcon}
              aria-label="AFE source overridden"
            />
          )}
          {displayContent}
          {onEdit && row.slotId && (
            <FloatingTooltip content="Edit AFE Source" placement="top">
              <button
                type="button"
                className={styles.editButton}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.slotId) onEdit(row.slotId);
                }}
                disabled={isEditDisabled}
                aria-label="Edit AFE source"
              >
                <Edit />
              </button>
            </FloatingTooltip>
          )}
        </div>
      );
    },
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip:
        'AFE Source can be configured per slot. Edits show with a blue background.',
    },
  },
  afeWellName: {
    header: 'AFE Well Name',
    cell: (info: CellContext<TractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    size: 250,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
  slotContingenciesCosts: {
    header: 'Slot Contingencies Costs',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatCurrency(info.getValue())}</CellRenderer>
    ),
    size: 190,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  contingenciesCostsDate: {
    header: 'Contingencies Costs Date',
    cell: (info: CellContext<TractNRI, string>) =>
      costDateCell(info, 'contingenciesCostsDate', 'Contingencies Costs Date'),
    filterFn: isSameDate,
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  slotFacilitiesCosts: {
    header: 'Slot Facilities Costs',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatCurrency(info.getValue())}</CellRenderer>
    ),
    size: 170,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  facilitiesCostsDate: {
    header: 'Facilities Costs Date',
    cell: (info: CellContext<TractNRI, string>) =>
      costDateCell(info, 'facilitiesCostsDate', 'Facilities Costs Date'),
    filterFn: isSameDate,
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  slotDrillingCosts: {
    header: 'Slot Drilling Costs',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatCurrency(info.getValue())}</CellRenderer>
    ),
    size: 170,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  drillingCostsDate: {
    header: 'Drilling Costs Date',
    cell: (info: CellContext<TractNRI, string>) =>
      costDateCell(info, 'drillingCostsDate', 'Drilling Costs Date'),
    filterFn: isSameDate,
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  slotCompletionCosts: {
    header: 'Slot Completion Costs',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatCurrency(info.getValue())}</CellRenderer>
    ),
    size: 190,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  completionCostsDate: {
    header: 'Completion Costs Date',
    cell: (info: CellContext<TractNRI, string>) =>
      costDateCell(info, 'completionCostsDate', 'Completion Costs Date'),
    filterFn: isSameDate,
    size: 200,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
  slotOtherCosts: {
    header: 'Slot Other Costs',
    cell: (info: CellContext<TractNRI, number>) => (
      <CellRenderer>{formatCurrency(info.getValue())}</CellRenderer>
    ),
    size: 170,
    enableColumnFilter: true,
    enableGlobalFilter: false,
    meta: {
      filterVariant: 'range',
    },
  },
  otherCostsDate: {
    header: 'Other Costs Date',
    cell: (info: CellContext<TractNRI, string>) =>
      costDateCell(info, 'otherCostsDate', 'Other Costs Date'),
    filterFn: isSameDate,
    size: 180,
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
      infoTooltip: 'Configurable per slot. Edited values are highlighted blue.',
    },
  },
} as const;

export const subtractNRIColumnConfig = {
  crmSubtractId: {
    header: 'CRM Subtract',
    size: 200,
    cell: (info: CellContext<SubtractNRI, string>) => {
      const { crmSubtractSalesforceId } = info.row.original;
      const value = info.getValue();
      // Link to the same full-page CRM Tract Wizard route as the
      // `crmTract` column above; the route accepts either a tract or
      // subtract Salesforce id under `:tract_id`. Replaces the prior
      // `<a href="#" onClick={onTractClick}>` pattern that opened the
      // legacy modal and presented as a broken link.
      if (crmSubtractSalesforceId && value) {
        return (
          <Link
            to={`/crm/tracts_and_subtracts/${encodeURIComponent(
              crmSubtractSalesforceId
            )}/edit`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.cellLabel}
          >
            {value}
          </Link>
        );
      }

      return <CellRenderer>{value}</CellRenderer>;
    },
  },
  uat: {
    header: 'UAT',
    size: 200,
    cell: (info: CellContext<SubtractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
  },
  uatNri: {
    header: 'UAT NRI',
    size: 280,
    cell: (info: CellContext<SubtractNRI, number>) => {
      const value = info.getValue();
      const row = info.row.original;
      const nriCalculationId = row.nriCalculationId;
      const verifications = row.verifications;

      return (
        <CellRenderer>
          <div className={styles.columnActionContainer}>
            {formatNumberWithTooltip(value, 8)}
            {nriCalculationId && (
              <LazyUnitInterestCard
                nriCalculationId={nriCalculationId}
                unitInterestValue={value ?? null}
                calculationType="NRI"
                renderTrigger={renderCalculationTrigger}
              />
            )}
            {nriCalculationId && (
              <UatNriMatrixCard
                nriCalculationId={nriCalculationId}
                calculationType="NRI"
              />
            )}
            <NRIVerification
              nriId={`${nriCalculationId}-nri`}
              disableActions={isNil(value)}
              eventsHref={verifications?.nri.href.events ?? ''}
              commentsHref={verifications?.nri.href.comments ?? ''}
              initialStatus={verifications?.nri.status ?? null}
              initialCommentsCount={verifications?.nri.commentsCount ?? 0}
            />
          </div>
        </CellRenderer>
      );
    },
  },
  uatWi: {
    header: 'UAT WI',
    size: 280,
    cell: (info: CellContext<SubtractNRI, number>) => {
      const value = info.getValue();
      const row = info.row.original;
      const nriCalculationId = row.nriCalculationId;
      const verifications = row.verifications;

      return (
        <CellRenderer>
          <div className={styles.columnActionContainer}>
            {formatNumberWithTooltip(value, 8)}
            {nriCalculationId && (
              <LazyUnitInterestCard
                nriCalculationId={nriCalculationId}
                unitInterestValue={value ?? null}
                calculationType="WI"
                renderTrigger={renderCalculationTrigger}
              />
            )}
            {nriCalculationId && (
              <UatNriMatrixCard
                nriCalculationId={nriCalculationId}
                calculationType="WI"
              />
            )}
            <NRIVerification
              nriId={`${nriCalculationId}-wi`}
              disableActions={isNil(value)}
              eventsHref={verifications?.workingInterest.href.events ?? ''}
              commentsHref={verifications?.workingInterest.href.comments ?? ''}
              initialStatus={verifications?.workingInterest.status ?? null}
              initialCommentsCount={
                verifications?.workingInterest.commentsCount ?? 0
              }
            />
          </div>
        </CellRenderer>
      );
    },
  },
  subtractNetAcres: {
    header: 'Subtract Net Acres',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  subtractGrossAcres: {
    header: 'Subtract Gross Acres',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  quarterCall: {
    header: 'Quarter Call',
    size: 150,
    cell: (info: CellContext<SubtractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
  },
  partTractFactor: {
    header: 'Part. Tract Factor',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  partTractAcres: {
    header: 'Part. Tract Acres',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  leaseRoyaltyRate: {
    header: 'Lease Royalty Rate',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  leaseRoyaltyType: {
    header: 'Lease Royalty Type',
    size: 180,
    cell: (info: CellContext<SubtractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
  },
  ptfAdjustment: {
    header: 'PTF Adjustment',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatPercentage(info.getValue())}</CellRenderer>
    ),
  },
  unitCoverage: {
    header: 'Unit Coverage',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatPercentage(info.getValue())}</CellRenderer>
    ),
  },
  uatCoverage: {
    header: 'UAT Coverage',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatPercentage(info.getValue())}</CellRenderer>
    ),
  },
  focusMdac: {
    header: 'Focus - MDAC',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatPercentage(info.getValue())}</CellRenderer>
    ),
  },
  executive: {
    header: 'Executive',
    size: 200,
    cell: (info: CellContext<SubtractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
  },
  bonus: {
    header: 'Bonus',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  delay: {
    header: 'Delay',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  royalty: {
    header: 'Royalty',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  royaltyBurdens: {
    header: 'Royalty Burdens',
    size: 180,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  wi: {
    header: 'WI',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  orri: {
    header: 'ORRI',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  orriBurdens: {
    header: 'ORRI Burdens',
    size: 150,
    cell: (info: CellContext<SubtractNRI, number>) => (
      <CellRenderer>{formatVariable(info.getValue())}</CellRenderer>
    ),
  },
  interestType: {
    header: 'Interest Type',
    size: 150,
    cell: (info: CellContext<SubtractNRI, string>) => (
      <CellRenderer>{info.getValue()}</CellRenderer>
    ),
    enableColumnFilter: true,
    enableGlobalFilter: true,
    meta: {
      filterVariant: 'text',
    },
  },
} as const;
